import { Injectable } from '@angular/core';
import { mppRecipeCard } from '@app/mpp/model/recipe-card-model';
import { createSelector, Store } from '@ngrx/store';
import {
  getAllRecipeFilters,
  getAllRecipes,
  getIsRecipeLoaded,
  getMPPBaseViewModel,
  getRecipeByIdFromState,
  getRecipeSortableHeaders,
  RecipeSortOptions,
  SortDirections,
  getCopiedRecipes,
  getRecipeDetails,
  RecipeDetails,
  getIngreidentSearchKey,
  getFilteredRecipeDataForSearchBasedOnBlacklist,
  selectRecipeImageSrc,
  recipeSRC,
  selectLastSelectedIngredient,
  getAllMPPProducts,
  selectBaseCalculations,
} from '@usf/ngrx-tools';
import {
  convertRecipesSortHeaders,
  getSortValueForModal,
  sortRecipesByheader,
  convertToRecipeCardViewModel,
  convertToRecipeDetailViewModel,
  ingredientNameSortAZ,
} from '../helper/recipe-view-model-helper';
import {
  PlatformEnum,
  PlatformService,
  selectCustomer,
  selectOnline,
} from '@panamax/app-state';
import { IngredientList, ItemType, MppProduct, Recipe } from '@usf/mpp-types';
import { selectUserKind } from '@app/user/store';
import { IngredientCard } from '@app/mpp/model/ingredient-card-model';
import {
  productSelector,
  productWithAlternativesSelector,
} from '@shared/selectors/product.selectors';
import { getAllNonUSFProductsAndConvertToFakeProducts } from '@inventory/selectors/inventory-view-model.selector';
import { combineMapAndArrayForNonUSFProducts } from '@inventory/selectors/helpers/inventory-view-model-helper';
import { Product } from '@shared/models/product.model';
import { IngredientSearchResult } from '@app/mpp/shared/model/ingredient-search-result';

@Injectable({
  providedIn: 'root',
})
export class RecipeViewModelSelectors {
  constructor(private platformService: PlatformService) {}

  private getRecipeCardData$ = createSelector(getAllRecipes(), recipes => {
    return recipes.map((recipe, index) => {
      return convertToRecipeCardViewModel(recipe, index);
    });
  });

  public getGroupsForRecipes$ = createSelector(
    this.getRecipeCardData$,
    recipes => {
      const groupsToFill: Set<string> = new Set();
      recipes.forEach(item => {
        if (!!item.groupName) groupsToFill.add(item.groupName);
      });
      return [...groupsToFill];
    },
  );

  private doRecipesExistInState$ = createSelector(getAllRecipes(), recipes => {
    return recipes.length > 0;
  });

  private getFilteredRecipeCardData$ = createSelector(
    this.getRecipeCardData$,
    getAllRecipeFilters(),
    (recipes, { filterArchive, groups, search }) => {
      return recipes.filter(item => {
        // if archiveInd true then filter out all archived items
        if ((item.archiveInd ?? false) === filterArchive) return false;

        if (groups.length > 0 && !groups.includes(item.groupName)) return false;

        if (search === '') return true;

        const keyWords = search.toUpperCase().split(' ');
        const itemSearchableWords =
          item?.recipeName?.toUpperCase().split(' ') ?? [];
        return keyWords.every(key => {
          return !!itemSearchableWords.find(value => {
            return !!value.includes(key);
          });
        });
      });
    },
  );

  // need to filter before this
  private getSortedRecipeCardData$ = createSelector(
    this.getFilteredRecipeCardData$,
    getRecipeSortableHeaders(),
    (recipes, sort) => {
      const selected = sort.recipes.selectedHeader as RecipeSortOptions;
      const direction = sort.recipes.headers[selected]
        .sortDirection as SortDirections;
      const headers = sort.recipes.headers;
      const sortedRecipes: mppRecipeCard[] = sortRecipesByheader(
        selected,
        direction,
        recipes,
      );
      return {
        headers,
        selected,
        direction,
        sortedRecipes,
      };
    },
  );

  public recipeMPPViewModel$ = createSelector(
    getMPPBaseViewModel,
    this.getSortedRecipeCardData$,
    getIsRecipeLoaded(),
    this.doRecipesExistInState$,
    getAllRecipeFilters(),
    this.getGroupsForRecipes$,
    selectUserKind,
    (
      baseViewModel,
      sortData,
      loaded,
      doRecipesExist,
      { filterArchive, groups, search },
      allGroups,
      userKind,
    ) => {
      const selectedRecipes = sortData.sortedRecipes.filter(
        recipe => recipe.isSelected,
      );

      const isEverythingShownSelected = sortData.sortedRecipes.every(item => {
        return item.isSelected;
      });
      // will replace with non magic numbers if this fix works.
      const itemHeights = [];
      switch (this.platformService.platformType) {
        case PlatformEnum.desktop:
          // header
          itemHeights.push(186);
          // cards
          sortData.sortedRecipes.forEach(ignore => {
            itemHeights.push(88);
          });
          break;
        case PlatformEnum.tablet:
          //header
          itemHeights.push(203);
          // cards
          sortData.sortedRecipes.forEach(ignore => {
            itemHeights.push(116);
          });
          break;
        case PlatformEnum.mobile:
          //header
          itemHeights.push(132);
          // cards
          sortData.sortedRecipes.forEach(ignore => {
            itemHeights.push(91);
          });
          break;
      }
      const itemSize =
        this.platformService.platformType ===
        this.platformService.platformEnum.desktop
          ? 88
          : 91;
      const sectionHeaders = convertRecipesSortHeaders(
        this.platformService.platformType,
        sortData.headers,
      );

      const sortSelected = getSortValueForModal(
        sortData.selected as RecipeSortOptions,
        sortData.direction,
      );

      return {
        ...baseViewModel,
        recipes: sortData.sortedRecipes,
        itemHeights,
        selectedRecipes,
        loaded,
        selectedHeader: sortData.selected,
        sectionHeaders,
        searchKey: search,
        isEverythingShownSelected,
        allGroups,
        filterArchive,
        sortSelected,
        doRecipesExist,
        filteredGroups: groups,
        userKind,
      };
    },
  );

  public getSearchResultsForIngredient$ = createSelector(
    getFilteredRecipeDataForSearchBasedOnBlacklist(),
    getIngreidentSearchKey(),
    (
      recipes: { recipeData: IngredientSearchResult[]; blackList: Set<string> },
      searchKey: string,
    ) => {
      const searchLimit =
        this.platformService.platformType ===
        this.platformService.platformEnum.desktop
          ? 5
          : 3;
      if (searchKey === '' || searchKey === null || searchKey === undefined) {
        return recipes.recipeData
          .sort(ingredientNameSortAZ)
          .slice(0, searchLimit);
      }

      const searchSet = searchKey.toUpperCase().split(' ');
      const filteredRecipes = recipes.recipeData.filter(recipe => {
        const recipeNameSet = recipe.name.toUpperCase().split(' ');
        return searchSet.every(
          matchData =>
            !!recipeNameSet.find(searchData => {
              return !!~searchData?.indexOf(matchData);
            }),
        );
      });
      return filteredRecipes.sort(ingredientNameSortAZ).slice(0, searchLimit);
    },
  );

  public getDataForIngredients = createSelector(
    productSelector,
    getAllNonUSFProductsAndConvertToFakeProducts,
    getAllRecipes(),
    getRecipeByIdFromState(),
    (
      products: Map<number, Product>,
      nonUSFProducts: Product[],
      recipes: Recipe[],
      recipe: Recipe,
    ) => {
      const prodMap = combineMapAndArrayForNonUSFProducts(
        products,
        nonUSFProducts,
      );

      const rawIngredients = recipe?.ingredientList ?? [];
      const data: IngredientCard[] = [];
      let index = 0;
      rawIngredients.forEach(ingredient => {
        let extractedData: { itemName: string; brand: string; cost: number } = {
          itemName: '',
          brand: '',
          cost: 0,
        };
        switch (ingredient.type) {
          case ItemType.RECIPE:
            const recipe = recipes.find(
              item => item.recipeId === ingredient.itemNumber,
            );
            if (!!recipe) {
              extractedData = {
                itemName: recipe.recipeName,
                brand: '', // will always be blank for recipes
                cost: ingredient.cost ?? 0,
              };
            }
            // ingredient.cost ?? recipe.cost,
            break;
          case ItemType.USFPRODUCT:
          case ItemType.NONUSFPRODUCT:
            let numberForProduct = Number(ingredient.itemNumber);
            if (prodMap.has(numberForProduct)) {
              const product = prodMap.get(numberForProduct);
              extractedData = {
                itemName:
                  product?.summary?.productDescTxtl ??
                  product?.summary?.productDescLong ??
                  '',
                brand: product?.summary?.brand ?? '',
                cost: ingredient.cost ?? 0,
              };
            }
            // ingredient.cost,
            break;
        }
        data.push({
          ...ingredient,
          ...extractedData,
          index,
          displayIndex: index + 1,
          readableType: this.transformTypeToReadable(ingredient.type),
        });
        index++;
      });

      return data;
    },
  );

  public getProductDataForRecipe$ = createSelector(
    getRecipeByIdFromState(),
    productSelector,
    getAllMPPProducts(),
    selectLastSelectedIngredient(),
    (
      recipe: Recipe,
      products: Map<number, Product>,
      mppState,
      selectedIndex: number | undefined,
    ) => {
      let lastSelectedProduct: Product;
      let lastSelectedMPP: MppProduct;
      let productNumber = 0;
      if (
        recipe?.ingredientList?.length - 1 >= selectedIndex &&
        recipe?.ingredientList[selectedIndex].type === ItemType.USFPRODUCT
      ) {
        productNumber = Number(
          recipe?.ingredientList[selectedIndex]?.itemNumber ?? 0,
        );
      }
      if (selectedIndex !== undefined && products.has(productNumber)) {
        lastSelectedProduct = products.get(productNumber);
      }

      if (!!productNumber) {
        lastSelectedMPP = mppState.find(
          item => item.productNumber === productNumber,
        );
      }
      return { lastSelectedProduct, lastSelectedMPP, selectedIndex };
    },
  );

  public recipeDetailViewModel$ = createSelector(
    getRecipeByIdFromState(),
    getRecipeDetails(),
    this.getDataForIngredients,
    this.getGroupsForRecipes$,
    selectRecipeImageSrc(),
    selectOnline(),
    selectCustomer(),
    this.getProductDataForRecipe$,
    selectBaseCalculations(),
    (
      recipe: Recipe,
      details: RecipeDetails,
      transformedIngredientData: IngredientCard[],
      groups: string[],
      recipeSRC: Record<string, recipeSRC>,
      online: boolean,
      selectedCustomer,
      lastSelectedProducts,
      calculations,
    ) => {
      const pricingDisabled = selectedCustomer?.ogPrintPriceInd === 'N';
      let lastSelectedProduct = lastSelectedProducts.lastSelectedProduct;
      let lastSelectedMPP = lastSelectedProducts.lastSelectedMPP;
      return convertToRecipeDetailViewModel(
        recipe,
        details,
        transformedIngredientData,
        this.platformService.platformType,
        groups,
        recipeSRC,
        online,
        pricingDisabled,
        lastSelectedProducts.selectedIndex,
        lastSelectedProduct,
        lastSelectedMPP,
        calculations,
      );
    },
  );
  public copiedRecipes$ = createSelector(getCopiedRecipes(), recipes => {
    return recipes;
  });

  public transformTypeToReadable(item: ItemType) {
    switch (item) {
      case ItemType.NONUSFPRODUCT:
        return 'Non US Foods';
      case ItemType.RECIPE:
        return 'Recipe';
      case ItemType.USFPRODUCT:
        return 'US Foods';
    }
  }
}
